import Button from '../components/ui/Button';

const PALETTES = [
  { name: 'primary',   label: 'Primary' },
  { name: 'secondary', label: 'Secondary' },
  { name: 'success',   label: 'Success' },
  { name: 'danger',    label: 'Danger' },
  { name: 'warning',   label: 'Warning' },
  { name: 'neutral',   label: 'Neutral' },
];

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

function Swatch({ palette, shade }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`w-10 h-10 rounded-clay-sm shadow-clay dark:shadow-clay-dark bg-${palette}-${shade}`}
      />
      <span className="text-[10px] text-neutral-500 dark:text-neutral-400 font-mono">{shade}</span>
    </div>
  );
}

function PaletteRow({ name, label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-20 text-xs font-medium text-neutral-600 dark:text-neutral-300 shrink-0">{label}</span>
      <div className="flex gap-2 flex-wrap">
        {SHADES.map(shade => (
          <Swatch key={shade} palette={name} shade={shade} />
        ))}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white dark:bg-white/5 rounded-clay shadow-clay dark:shadow-clay-dark border border-white/60 dark:border-transparent p-6">
      <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-widest mb-5">{title}</h2>
      {children}
    </section>
  );
}

function CssVarRow({ name }) {
  return (
    <div className="flex items-center gap-3 text-xs font-mono">
      <span className="text-neutral-400 dark:text-neutral-500 w-56 shrink-0">{name}</span>
      <span
        className="text-neutral-700 dark:text-neutral-300"
        ref={el => {
          if (el) el.textContent = getComputedStyle(document.documentElement).getPropertyValue(name).trim() || '—';
        }}
      />
    </div>
  );
}

const KEY_VARS = [
  '--theme-bg-color', '--theme-text-color', '--theme-heading-color',
  '--color-primary-50', '--color-primary-400', '--color-primary-600', '--color-primary-900',
  '--color-secondary-400', '--color-secondary-600',
  '--nav-bg', '--nav-brand-text', '--nav-link-active-text',
  '--shadow-clay', '--shadow-clay-btn',
];

export default function DesignTest() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">
      <div>
        <p className="text-xs font-mono text-neutral-400 dark:text-neutral-500 mb-1">design-trials</p>
        <h1 className="text-2xl font-bold">Design Test</h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Live preview of all theme tokens. Edit <code className="font-mono text-xs bg-neutral-100 dark:bg-white/8 px-1.5 py-0.5 rounded">src/theme.js</code> then hard-refresh (Cmd+R) to see changes.
        </p>
      </div>

      {/* Color palettes */}
      <Section title="Color Palettes">
        <div className="space-y-4">
          {PALETTES.map(p => <PaletteRow key={p.name} {...p} />)}
        </div>
      </Section>

      {/* Buttons */}
      <Section title="Button Variants">
        <div className="space-y-4">
          {['primary','secondary','success','danger','ghost','outline'].map(variant => (
            <div key={variant} className="flex flex-wrap items-center gap-3">
              <span className="w-20 text-xs font-medium text-neutral-500 dark:text-neutral-400 shrink-0">{variant}</span>
              <Button variant={variant} size="sm">Small</Button>
              <Button variant={variant} size="md">Medium</Button>
              <Button variant={variant} size="lg">Large</Button>
              <Button variant={variant} disabled>Disabled</Button>
            </div>
          ))}
        </div>
      </Section>

      {/* Typography */}
      <Section title="Typography">
        <div className="space-y-3">
          {[1,2,3,4].map(n => {
            const Tag = `h${n}`;
            const sizes = { 1: 'text-3xl', 2: 'text-2xl', 3: 'text-xl', 4: 'text-lg' };
            return (
              <Tag key={n} className={`${sizes[n]} font-bold`}>Heading {n} — The quick brown fox</Tag>
            );
          })}
          <p className="text-base leading-7">
            Body text — OptionView is a free toolkit for Indian options traders. The goal is simple: make the data and math behind options accessible.{' '}
            <a href="#" className="text-primary-600 dark:text-primary-400 hover:underline">A sample link</a>.
          </p>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Small / muted text — Last updated: 27 Mar 2026 · IV Rank: 42nd percentile
          </p>
          <code className="block text-xs font-mono bg-neutral-100 dark:bg-white/8 px-3 py-2 rounded-clay-sm">
            const iv = blackScholes(spot, strike, expiry, rate, sigma);
          </code>
        </div>
      </Section>

      {/* Shadows */}
      <Section title="Shadows">
        <div className="flex flex-wrap gap-6">
          {[
            { label: 'shadow-clay',     cls: 'shadow-clay' },
            { label: 'shadow-clay-hover', cls: 'shadow-clay-hover' },
            { label: 'shadow-clay-btn', cls: 'shadow-clay-btn' },
            { label: 'shadow-clay-dark (→ dark mode)',  cls: 'shadow-clay-dark' },
          ].map(({ label, cls }) => (
            <div key={label} className="flex flex-col items-center gap-2">
              <div className={`w-20 h-20 rounded-clay bg-white dark:bg-white/10 ${cls}`} />
              <span className="text-[10px] font-mono text-neutral-400 dark:text-neutral-500">{label}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Cards */}
      <Section title="Card Styles">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/50 rounded-clay p-4">
            <p className="text-sm font-semibold text-primary-800 dark:text-primary-200">Primary card</p>
            <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">bg-primary-50 / border-primary-200</p>
          </div>
          <div className="bg-secondary-50 dark:bg-secondary-900/20 border border-secondary-200 dark:border-secondary-800/50 rounded-clay p-4">
            <p className="text-sm font-semibold text-secondary-800 dark:text-secondary-200">Secondary card</p>
            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">bg-secondary-50 / border-secondary-200</p>
          </div>
          <div className="bg-white dark:bg-white/5 border border-white/60 dark:border-transparent rounded-clay shadow-clay dark:shadow-clay-dark p-4">
            <p className="text-sm font-semibold">Neutral card</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">bg-white + shadow-clay</p>
          </div>
        </div>
      </Section>

      {/* Active CSS vars */}
      <Section title="Active CSS Variables">
        <div className="space-y-2">
          {KEY_VARS.map(v => <CssVarRow key={v} name={v} />)}
        </div>
      </Section>
    </div>
  );
}
